import { ChangeDetectionStrategy, Component, inject, Inject, DOCUMENT } from '@angular/core';
import { ISizeName } from '@df/core/service/client';
import { StylesService } from '@df/core/service/styles';
import { Status } from '@df/core/status';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { ImageModule } from '@df/module-image/image.module';
import { IFileInputEvent } from '@df/ui/form/file-input.component.def';
import { FileLoadService } from '@df/ui/form/file-upload/file-load.service';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ActionComponent } from 'projects/app-default/src/app/ui/api/action.component';
import { FileInputComponent } from 'projects/app-default/src/app/ui/form/file-input.component';
import { ModalLayoutRightComponent, NModalLayoutRight } from 'projects/app-default/src/app/ui/modal/modal-layout-right.component';
import { ResultComponent } from 'projects/app-default/src/app/ui/resut/result.component';

export namespace NGiftcardsPersonaliseModal {
    export const MODAL_ID = 'giftcards-personalise-modal';
    export interface IModalData {
        previewImage: string;
        request: Giftcards.ISelectionContainer;
    }
}

@DfModal({
    id: NGiftcardsPersonaliseModal.MODAL_ID,
    position: (size: ISizeName) => (size === 'S' ? 'bottom' : 'right'),
    config: (size: ISizeName) =>
        size === 'S'
            ? {
                  closeOnNavigation: true,
                  height: '80vh',
                  maxHeight: '650px',
                  width: '100vw',
                  hasBackdrop: true,
                  modalClass: 'p-a-0',
                  maxWidth: undefined,
                  position: {
                      bottom: '0',
                      right: '0',
                      left: '0'
                  }
              }
            : {
                  ...NModalLayoutRight.ModalConfig,
                  width: { S: StylesService.vw(100), M: StylesService.vw(72), L: '505px', X: '505px' }[size]
              },
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeId => ({ S: ['S'], M: ['M', 'L', 'X'], L: ['M', 'L', 'X'], X: ['M', 'L', 'X'] })[sizeId]
})
@Component({
    templateUrl: './giftcards-personalise-modal.html',
    styleUrls: ['./giftcards-personalise-modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ModalLayoutRightComponent, TranslationModule, ImageModule, FileInputComponent, ActionComponent, ResultComponent]
})
export class GiftcardsPersonaliseModalComponent extends UiComponent {
    protected uploadButtonStatus = new Status();
    private fileLoadService = inject(FileLoadService);
    protected imageSrc: string | ArrayBuffer | undefined;
    readonly logoSrc = 'assets/images/giftcard-logo.svg'; // Path to logo image
    protected canvaBase64: string | undefined;

    /**
     * Close giftcards-personalise-modal
     */
    constructor(
        private modalRef: ModalRef<GiftcardsPersonaliseModalComponent>,
        @Inject(MODAL_DATA) readonly data: NGiftcardsPersonaliseModal.IModalData,
        @Inject(DOCUMENT)
        private documentRef: Document
    ) {
        super();
    }

    /**
     * Close giftcards-personalise-modal
     */
    close() {
        this.modalRef.close();
    }

    onFileChange(event: IFileInputEvent): void {
        this.uploadButtonStatus.busy = true;
        this.changeDetectorRef.detectChanges();
        if (event.error) {
            this.uploadButtonStatus.error = event.error.message;
            this.detectChanges();
        } else if (event.file) {
            const subscription = this.fileLoadService.load(this.uploadButtonStatus, event.file, 'url').subscribe(result => {
                if (result.result) {
                    subscription.unsubscribe();
                    this.imageSrc = result.result;
                    this.drawImageWithOverlay();
                }
            });
        }
    }

    // Draw image and overlay on canvas
    drawImageWithOverlay() {
        const canvas = this.documentRef.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx || !this.imageSrc) {
            return;
        }

        const image = new Image();
        image.src = this.imageSrc.toString();

        image.onload = () => {
            // Set canvas size to match the image
            canvas.width = image.width;
            canvas.height = image.height;

            // Draw the uploaded image onto the canvas
            ctx.drawImage(image, 0, 0);

            // Fill canvas background (optional, in case you want to fill letterbox areas with color)
            ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'; // Transparent black (optional)
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Load and draw the logo overlay in white (centered)
            const logo = new Image();
            logo.src = this.logoSrc;

            logo.onload = () => {
                // Convert the SVG logo to white (using filters or CSS applied to the image)
                ctx.filter = 'brightness(0) invert(1)'; // Make the logo white

                // Set the size for the logo (e.g., 33% of the canvas width)
                const logoWidth = canvas.width * 0.33;
                const logoHeight = logo.height * (logoWidth / logo.width);

                // Center the logo on the canvas
                const logoX = (canvas.width - logoWidth) / 2;
                const logoY = (canvas.height - logoHeight) / 2;

                // Draw the logo on top of the image and overlay
                ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);

                // Reset filter after drawing logo
                ctx.filter = 'none';

                // Convert canvas to a file format (e.g., Blob) and send to backend
                this.canvaBase64 = canvas.toDataURL('image/jpeg', 1);
                this.uploadButtonStatus = new Status();
                this.detectChanges();
            };
        };
    }

    protected confirm() {
        if (this.canvaBase64) {
            this.data.request.file = this.canvaBase64;
            this.close();
        }
    }
}
